import React, { useRef } from "react";
import styles from "./GreenSwitch.module.css";

const withAlpha = (color, alpha) => {
  if (!color) {
    return `rgba(0, 0, 0, ${alpha})`;
  }
  if (color.startsWith("#")) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (color.startsWith("rgb")) {
    return color.replace(/^rgba?\((\d+),\s*(\d+),\s*(\d+).*\)$/, `rgba($1, $2, $3, ${alpha})`);
  }
  return color;
};

/**
 * @param {import('react').InputHTMLAttributes<HTMLInputElement> & {
 *   checked?: boolean | number,
 *   onChange?: (e: import('react').ChangeEvent<HTMLInputElement>) => void,
 *   disabled?: boolean,
 *   label?: import('react').ReactNode,
 *   color?: string,
 *   value?: string | number | boolean,
 *   checkedLabel?: import('react').ReactNode,
 *   uncheckedLabel?: import('react').ReactNode,
 *   disableRipple?: boolean,
 * }} props
 */
const GreenSwitch = ({
  checked,
  onChange,
  disabled = false,
  label = undefined,
  color = undefined,
  uncheckedColor = undefined,
  value,
  checkedLabel = undefined,
  uncheckedLabel = undefined,
  ...rest
}) => {
  const inputRef = useRef(null);

  const handleToggle = (e) => {
    if (e.target === inputRef.current) return;
    e.preventDefault();
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleChange = (e) => {
    onChange?.(e);
  };

  const checkedColor = color || "#70CB93";
  const offColor = uncheckedColor || "#AFA3AF";

  const trackStyle = {
    backgroundColor: checked
      ? withAlpha(checkedColor, 0.25)
      : withAlpha(offColor, 0.25),
  };
  const thumbStyle = {
    backgroundColor: checked ? checkedColor : offColor,
  };
  const statusStyle = checked ? { color: checkedColor } : undefined;

  return (
    <label
      className={[styles.wrapper, disabled && styles.disabled]
        .filter(Boolean)
        .join(" ")}
    >
      {label && <span className={styles.switchLabel}>{label}</span>}
      <span className={styles.switchRoot} onClick={handleToggle}>
        <input
          ref={inputRef}
          type="checkbox"
          className={styles.hiddenInput}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          {...rest}
        />
        <div
          className={[styles.track, checked && styles.checked]
            .filter(Boolean)
            .join(" ")}
          style={trackStyle}
        >
          <div
            className={[styles.thumb, checked && styles.checked]
              .filter(Boolean)
              .join(" ")}
            style={thumbStyle}
          />
        </div>
      </span>
      {(checkedLabel || uncheckedLabel) && (
        <span
          className={[styles.statusLabel, checked && styles.checked]
            .filter(Boolean)
            .join(" ")}
          style={statusStyle}
        >
          {checked ? checkedLabel : uncheckedLabel}
        </span>
      )}
    </label>
  );
};

export default GreenSwitch;
